import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAgentPermission } from '@entities/user';
import {
  useApplyPtoRdImportMutation,
  useDeletePtoRdImportMutation,
  useGetPtoProjectQuery,
  useGetPtoRdImportQuery,
  useRecognizePtoRdImportMutation,
  type PtoRdImportDetail,
  type PtoRdImportPlan,
  type PtoRdImportStatus,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Progress } from '@shared/ui/Progress';
import { ApplyResult, hasSummary } from './ApplyResult';
import { errorText, httpStatus } from './errors';
import { plural } from './format';
import { MoreList, NoteList } from './NoteList';
import { PlanReview } from './PlanReview';
import { RdQueuePanel } from './RdQueuePanel';
import { isWorking, statusLabel, StatusTone } from './status';
import { reportFromState, type UploadReport } from './upload';
import { UploadBox } from './UploadBox';
import { useRdUpload } from './useRdUpload';
import {
  Code,
  ErrorText,
  LinkButton,
  List,
  Muted,
  Notice,
  NoticeTitle,
  Panel,
  Row,
  Section,
  SectionTitle,
  Small,
  Spread,
} from './styles';

/**
 * Интервал опроса карточки: 3 с — пакет в работе на сервере; 15 с — ревизии
 * пакета индексируются; иначе (загрузка файлов, проверка, ошибка, всё готово,
 * пакета нет) — не опрашиваем.
 */
function pollInterval(d: PtoRdImportDetail | undefined, gone: boolean): number {
  if (!d || gone) return 0;
  if (isWorking(d.import.status)) return 3000;
  const { ingest } = d;
  if (d.import.status === 'applied' && ingest && ingest.indexed + ingest.failed < ingest.total) {
    return 15000;
  }
  return 0;
}

function hasPlan(plan: Partial<PtoRdImportPlan> | undefined): plan is PtoRdImportPlan {
  return (
    !!plan &&
    Array.isArray(plan.tituls) &&
    Array.isArray(plan.complects) &&
    Array.isArray(plan.loose)
  );
}

/** Итог отправки файлов (приходит из загрузки через состояние навигации). */
function UploadNotes({
  report,
  status,
  onHide,
}: {
  report: UploadReport;
  status: PtoRdImportStatus;
  onHide: () => void;
}) {
  const { failed, warnings } = report;
  const recognizeError = status === 'uploading' ? report.recognizeError : null;
  const problems = Boolean(
    report.error || recognizeError || report.stopped || failed.length > 0 || report.notSent > 0,
  );
  if (!problems && warnings.length === 0 && report.skippedExisting === 0) return null;
  return (
    <Notice $tone={problems ? 'warn' : 'info'}>
      <Spread>
        <NoticeTitle>Отправка файлов</NoticeTitle>
        <LinkButton type="button" onClick={onHide}>
          скрыть
        </LinkButton>
      </Spread>
      {report.error ? <ErrorText>{report.error}</ErrorText> : null}
      {report.stopped ? <ErrorText>{report.stopped}</ErrorText> : null}
      {recognizeError ? (
        <ErrorText>
          Файлы загружены, но пакет не встал в очередь распознавания: {recognizeError} Нажмите
          «Распознать загруженное».
        </ErrorText>
      ) : null}
      <Muted>
        Отправлено файлов: {report.uploaded}
        {report.skippedExisting > 0 ? `; уже были в пакете — ${report.skippedExisting}` : ''}
        {failed.length > 0 ? `; не загрузились — ${failed.length}` : ''}
        {report.notSent > 0
          ? `; не отправлялись${report.aborted ? ' (загрузка прервана)' : ''} — ${report.notSent}`
          : ''}
        .
      </Muted>
      {failed.length > 0 ? (
        <MoreList
          items={failed}
          limit={20}
          itemKey={(f, i) => `${i}:${f.path}`}
          render={(f) => (
            <>
              <Code>{f.path}</Code> — {f.reason}
            </>
          )}
        />
      ) : null}
      {warnings.length > 0 ? (
        <>
          <Muted>Не вошли в пакет:</Muted>
          <NoteList items={warnings} />
        </>
      ) : null}
    </Notice>
  );
}

/**
 * Карточка пакета загрузки РД: очередь, распознавание, проверка, итог.
 * Ключ — id пакета: переход к другому пакету по ссылке сбрасывает черновик
 * раскладки и прерывает дозагрузку, а не переносит их в чужой пакет.
 */
export function PtoRdImportView() {
  const { importId = '' } = useParams();
  return <ImportCard key={importId} importId={importId} />;
}

function ImportCard({ importId }: { importId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const report = reportFromState(location.state);
  const { can_write } = useAgentPermission('pto');
  const [poll, setPoll] = useState(0);
  const {
    data,
    error: fetchError,
    isLoading,
    isFetching,
    refetch,
  } = useGetPtoRdImportQuery(importId, {
    skip: !importId,
    // Карточка лёгкая — при открытии всегда свежая, а не из кэша.
    refetchOnMountOrArgChange: true,
    pollingInterval: poll,
    skipPollingIfUnfocused: true,
  });
  const gone = httpStatus(fetchError) === 404;
  useEffect(() => setPoll(pollInterval(data, gone)), [data, gone]);
  const [recognize, { isLoading: recognizing }] = useRecognizePtoRdImportMutation();
  const [apply, { isLoading: applying }] = useApplyPtoRdImportMutation();
  const [remove, { isLoading: removing }] = useDeletePtoRdImportMutation();
  const projectId = data?.import.plan.project_id;
  const { currentData: projectData } = useGetPtoProjectQuery(projectId ?? '', {
    skip: !projectId,
  });
  const upload = useRdUpload();
  const [error, setError] = useState<string | null>(null);
  const pending = recognizing || applying || removing || upload.busy;

  if (!importId) return <ErrorText>Пакет не указан.</ErrorText>;
  if (isLoading) return <Muted>Загрузка пакета…</Muted>;
  if (!data) {
    // Экран ошибки — только когда показать нечего; с данными — плашка ниже.
    return (
      <div>
        <ErrorText>
          {gone
            ? 'Пакет не найден или нет доступа.'
            : errorText(fetchError, 'Не удалось загрузить пакет.')}
        </ErrorText>
        <Row>
          <Link to="/agents/pto/rd-import">
            <Button $variant="ghost">Все пакеты</Button>
          </Link>
          {!gone ? (
            <Button $variant="secondary" disabled={isFetching} onClick={() => void refetch()}>
              Повторить
            </Button>
          ) : null}
        </Row>
      </div>
    );
  }
  const imp = data.import;
  const progress = imp.progress ?? {};
  const summary = imp.summary ?? {};
  const fileCount = data.file_counts?.total ?? 0;
  const planReady = hasPlan(imp.plan);
  const legacyWithoutProject = !projectId && !planReady;
  const stampErrors = progress.stamp_errors ?? 0;

  /** 409 — пакет сменил состояние (его обрабатывают или он уже загружен): перечитать карточку. */
  function refreshOnConflict(e: unknown) {
    if (httpStatus(e) === 409) void refetch();
  }

  async function onRecognize(again: boolean) {
    if (!projectId) return;
    if (
      again &&
      !window.confirm(
        'Распознать пакет заново? Шифры и штампы прочитаются ещё раз, раскладка соберётся заново — правки раскладки будут сброшены.',
      )
    ) {
      return;
    }
    setError(null);
    try {
      await recognize(importId).unwrap();
    } catch (e) {
      setError(errorText(e, 'Не удалось поставить пакет в очередь распознавания.'));
      refreshOnConflict(e);
    }
  }

  async function onApply() {
    if (!projectId) return;
    setError(null);
    try {
      await apply(importId).unwrap();
    } catch (e) {
      setError(errorText(e, 'Не удалось поставить загрузку в очередь.'));
      refreshOnConflict(e);
    }
  }

  async function onDelete() {
    if (!window.confirm('Удалить пакет? Загруженные в реестр ревизии останутся.')) return;
    setError(null);
    try {
      await remove(importId).unwrap();
      navigate('/agents/pto/rd-import');
    } catch (e) {
      setError(errorText(e, 'Не удалось удалить пакет.'));
      refreshOnConflict(e);
    }
  }

  const hideReport = () => navigate(location.pathname, { replace: true, state: null });
  const canDelete = can_write && imp.status !== 'recognizing' && imp.status !== 'applying';

  return (
    <div>
      <Spread>
        <div>
          <h1>{imp.source_name}</h1>
          {projectId ? (
            <Muted>
              Проект:{' '}
              <Link to={`/agents/pto/${projectId}`}>
                {projectData?.project.name ??
                  imp.plan.tituls?.find((t) => t.project_id === projectId)?.project_name ??
                  'Открыть проект пакета'}
              </Link>
            </Muted>
          ) : (
            <Muted>Проект старого пакета ещё не выбран.</Muted>
          )}
          <Row>
            <StatusTone status={imp.status}>{statusLabel(imp.status)}</StatusTone>
            <Small>
              {fileCount} {plural(fileCount, ['файл', 'файла', 'файлов'])} в пакете
            </Small>
            <Small>создан {new Date(imp.created_at).toLocaleString('ru-RU')}</Small>
          </Row>
        </div>
        <Row>
          <Link
            to="/agents/pto/rd-import"
            onClick={(e) => {
              if (upload.busy && !window.confirm('Отправка файлов прервётся. Уйти со страницы?')) {
                e.preventDefault();
              }
            }}
          >
            <Button $variant="ghost">Все пакеты</Button>
          </Link>
          {canDelete ? (
            <Button $variant="ghost" disabled={pending} onClick={() => void onDelete()}>
              Удалить пакет
            </Button>
          ) : null}
        </Row>
      </Spread>
      {error ? <ErrorText>{error}</ErrorText> : null}
      {fetchError ? (
        <Notice $tone={gone ? 'bad' : 'warn'} role="status">
          {gone ? (
            'Пакет больше не найден — возможно, его удалили. Обновление остановлено.'
          ) : (
            <>
              Не удалось обновить состояние пакета: {errorText(fetchError, 'ошибка связи.')}{' '}
              Показано последнее известное.{' '}
              <LinkButton type="button" disabled={isFetching} onClick={() => void refetch()}>
                Повторить
              </LinkButton>
            </>
          )}
        </Notice>
      ) : null}
      {report ? <UploadNotes report={report} status={imp.status} onHide={hideReport} /> : null}
      {legacyWithoutProject && (imp.status === 'uploading' || imp.status === 'failed') ? (
        <Notice $tone="warn">
          Пакет создан без выбора проекта. Начните новую загрузку и явно выберите проект.{' '}
          <Link to="/agents/pto/rd-import">Новая загрузка РД</Link>
        </Notice>
      ) : null}

      {imp.status === 'uploading' ? (
        <Section>
          <Panel>
            <Muted>
              Загрузка файлов не завершена{report?.aborted ? ' — вы её прервали' : ''}. Выберите тот
              же архив или папку ещё раз — уже загруженные файлы пропустятся, недостающие
              дозагрузятся, — или распознайте то, что загружено.
            </Muted>
          </Panel>
          {can_write ? (
            <>
              {!legacyWithoutProject ? (
                <Section>
                  <UploadBox
                    onFiles={(files) => void upload.start(files, importId)}
                    busy={upload.busy}
                    stage={upload.stage}
                    progress={upload.progress}
                    warnings={upload.warnings}
                    error={upload.error}
                    onCancel={upload.cancel}
                    hint="Дозагрузка в этот пакет: файлы, которые уже загружены (тот же путь и размер), пропустятся; новые добавятся, изменённые заменятся."
                  />
                </Section>
              ) : null}
              <Section>
                <Row>
                  <Button
                    $variant="primary"
                    disabled={pending || fileCount === 0 || !projectId}
                    onClick={() => void onRecognize(false)}
                  >
                    Распознать загруженное ({fileCount})
                  </Button>
                  {upload.busy ? <Small>Дождитесь конца отправки или прервите её.</Small> : null}
                  {!upload.busy && fileCount === 0 ? (
                    <Small>В пакете ещё нет файлов — загрузите архив или папку.</Small>
                  ) : null}
                </Row>
              </Section>
            </>
          ) : (
            <Muted>Дозагрузить пакет может инженер с правом записи в ОСНОВА.Инженер.</Muted>
          )}
        </Section>
      ) : null}

      {imp.status === 'queued' ? (
        <Section>
          <Panel>
            <Muted>
              Пакет в очереди на распознавание
              {data.queue_ahead ? ` — впереди пакетов: ${data.queue_ahead}` : ''}. Пакеты
              распознаются по одному; можно закрыть страницу — работа продолжится на сервере.
            </Muted>
          </Panel>
          <RdQueuePanel />
        </Section>
      ) : null}

      {imp.status === 'recognizing' ? (
        <Section>
          <Panel>
            <Muted>
              {progress.stage === 'names' ? (
                <>
                  Файлы распознаны. ИИ исправляет наименования, прочитанные со сканов, — каждое
                  исправление программа сверяет с текстом скана. Можно закрыть страницу.
                </>
              ) : (
                <>
                  Распознаём шифры и основные надписи: {progress.done ?? 0} из{' '}
                  {progress.total ?? fileCount} файлов. Сканы читаются распознаванием текста — это
                  дольше. Можно закрыть страницу.
                </>
              )}
            </Muted>
            <Progress
              percent={progress.total ? ((progress.done ?? 0) / progress.total) * 100 : 0}
            />
          </Panel>
        </Section>
      ) : null}

      {(imp.status === 'review' || imp.status === 'failed') && stampErrors > 0 ? (
        <Notice $tone="warn">
          У {stampErrors} {plural(stampErrors, ['файла', 'файлов', 'файлов'])} штамп не прочитан:
          сервис документов был недоступен, шифр взят из имени файла. Можно распознать заново.
          {can_write && imp.status === 'review' ? (
            <Row>
              <Button
                $variant="secondary"
                $size="sm"
                disabled={pending || !projectId}
                onClick={() => void onRecognize(true)}
              >
                Распознать заново
              </Button>
            </Row>
          ) : null}
        </Notice>
      ) : null}

      {imp.status === 'review' && hasSummary(summary) ? (
        <Notice $tone={summary.errors?.length ? 'bad' : 'info'}>
          <NoticeTitle>Прошлая попытка загрузки в ОСНОВУ</NoticeTitle>
          <ApplyResult summary={summary} />
          <Muted>
            Уже загруженные комплекты теперь отмечены «уже загружено». Исправьте раскладку и
            загрузите оставшиеся.
          </Muted>
        </Notice>
      ) : null}

      {imp.status === 'review' && planReady ? (
        <Section>
          <PlanReview importId={importId} plan={imp.plan as PtoRdImportPlan} canWrite={can_write} />
        </Section>
      ) : null}

      {imp.status === 'review' && !planReady ? (
        <Section>
          <Panel>
            <ErrorText>Раскладка пакета не получена.</ErrorText>
            {can_write ? (
              <Row>
                <Button
                  $variant="primary"
                  disabled={pending || !projectId}
                  onClick={() => void onRecognize(true)}
                >
                  Распознать заново
                </Button>
              </Row>
            ) : null}
          </Panel>
        </Section>
      ) : null}

      {imp.status === 'applying' ? (
        <Section>
          <Panel>
            <Muted>
              {progress.stage === 'apply'
                ? `Загружаем в реестр: комплектов ${progress.done ?? 0} из ${progress.total ?? 0}.`
                : 'Ждём очереди на загрузку в реестр: пакеты загружаются по одному.'}{' '}
              Шифры заново сверяются с реестром выбранного проекта — если кто-то уже загрузил тот же
              комплект, дубля не будет. Можно закрыть страницу.
            </Muted>
            {progress.stage === 'apply' && progress.total ? (
              <Progress percent={((progress.done ?? 0) / progress.total) * 100} />
            ) : null}
          </Panel>
        </Section>
      ) : null}

      {imp.status === 'applied' ? (
        <Section>
          <SectionTitle>Итог</SectionTitle>
          <Panel>
            <ApplyResult summary={summary} />
            {data.ingest?.total > 0 ? (
              <>
                <Muted>
                  Индексация: {data.ingest.indexed} из {data.ingest.total}
                  {data.ingest.failed ? `, с ошибкой — ${data.ingest.failed}` : ''}. Комплекты
                  индексируются по одному, чтобы не занимать очередь других инженеров.
                </Muted>
                <Progress
                  percent={
                    ((data.ingest.indexed + data.ingest.failed) / Math.max(1, data.ingest.total)) *
                    100
                  }
                />
              </>
            ) : null}
          </Panel>
          <RdQueuePanel compact />
        </Section>
      ) : null}

      {imp.status === 'failed' ? (
        <Section>
          <Panel>
            <ErrorText>{imp.error ?? 'Пакет не обработан.'}</ErrorText>
            {can_write ? (
              <>
                <Muted>Что можно сделать:</Muted>
                <List>
                  {planReady ? (
                    <li>
                      «Загрузить в ОСНОВУ» — повторить загрузку в реестр по сохранённой раскладке
                      (уже загруженное не задвоится);
                    </li>
                  ) : null}
                  <li>
                    «Распознать заново» — прочитать шифры и штампы ещё раз; раскладка соберётся
                    заново{planReady ? ', её правки сбросятся' : ''};
                  </li>
                  <li>«Удалить пакет» — и загрузить архив новым пакетом.</li>
                </List>
                <Row>
                  {planReady ? (
                    <Button
                      $variant="primary"
                      disabled={pending || !projectId}
                      onClick={() => void onApply()}
                    >
                      {applying ? 'Ставим в очередь…' : 'Загрузить в ОСНОВУ'}
                    </Button>
                  ) : null}
                  <Button
                    $variant={planReady ? 'secondary' : 'primary'}
                    disabled={pending || !projectId}
                    onClick={() => void onRecognize(planReady)}
                  >
                    Распознать заново
                  </Button>
                  <Button $variant="ghost" disabled={pending} onClick={() => void onDelete()}>
                    Удалить пакет
                  </Button>
                </Row>
              </>
            ) : (
              <Muted>
                Повторить загрузку или удалить пакет может инженер с правом записи в ОСНОВА.Инженер.
              </Muted>
            )}
          </Panel>
          {hasSummary(summary) ? (
            <Notice $tone={summary.errors?.length ? 'bad' : 'info'}>
              <NoticeTitle>Итог последней попытки</NoticeTitle>
              <ApplyResult summary={summary} />
            </Notice>
          ) : null}
          {planReady ? (
            <Section>
              <SectionTitle>Раскладка пакета</SectionTitle>
              <PlanReview
                importId={importId}
                plan={imp.plan as PtoRdImportPlan}
                canWrite={can_write}
              />
            </Section>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}
